import { Student } from '../utils/amplify/getStudents';
import { AuthUser, LevelDisplayName, Role } from '../@types/auth';
import { trpc } from '../utils/trpcAmplify';
import useAuth from './useAuth';

const isMultilevelGuide = (inputRole: Role, inputUser: AuthUser) =>
  inputRole === Role.GUIDE &&
  inputUser?.alphaAuthAttributes?.levels.find((l) => l.displayName === LevelDisplayName.Multilevel);

const getStudentIds = (user: AuthUser, role: Role): string[] => {
  if (role == Role.PARENT) {
    return user?.alphaAuthAttributes?.childStudentIds || [];
  } else if (role == Role.STUDENT) {
    const studentId = user?.alphaAuthAttributes?.studentId;
    return studentId ? [studentId] : [];
  }
  return [];
};
const getAlphaLevel = (user: AuthUser, role: Role): number[] | null => {
  const lookupString = user?.alphaAuthAttributes?.levels[0]?.lookupString;
  if ([Role.ADMIN, Role.GUIDE].includes(role)) {
    // Admins, BTX guides, and multilevel guides can see all levels
    if (
      [Role.ADMIN].includes(role) ||
      user?.alphaAuthAttributes?.campusId == '3' ||
      isMultilevelGuide(role as Role, user)
    ) {
      // Previously [0, 0.5, 1, 2, 3, 4], but this excluded 4.1/4.2/etc; empty array effectively wildcard
      return [];
    } else {
      // handling LL & linc guides
      if (lookupString === '<1') {
        return [0, 0.5];
      }
      // handling L4 guides
      if (lookupString === '>=4') {
        return [4]; // This will be converted into 4.1, 4.2, 4.3, 4.4 in the backend
      }
      // base case
      return [Number(lookupString)] || -1;
    }
  }
  return null;
};
const getCampusId = (user: AuthUser, role: Role): string | undefined => {
  if ([Role.ADMIN, Role.GUIDE].includes(role)) {
    return [Role.ADMIN].includes(role) || isMultilevelGuide(role as Role, user)
      ? '%'
      : user?.alphaAuthAttributes?.campusId;
  }
  return user?.alphaAuthAttributes?.campusId;
};

export default function useStudentList(): Student[] | undefined {
  const { user } = useAuth();
  const role = user?.alphaAuthAttributes?.role;

  const studentIds = getStudentIds(user, role as Role);
  const alphaLevel = getAlphaLevel(user, role as Role);
  const campusId = getCampusId(user, role as Role);

  const response = trpc.masterRoster.getStudents.useQuery({
    alphaLevel: alphaLevel || [],
    campusId: campusId || '',
    studentIds: studentIds,
  });
  return response.data;
}
